import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  Trash2,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";
import { useBuilder, BlockConfig } from "./BuilderContext";

interface FloatingToolbarProps {
  block: BlockConfig;
}

const LINE_ITEM_TYPES = ["fees_table", "disbursements_table", "time_entries_table"];

const FloatingToolbar: React.FC<FloatingToolbarProps> = ({ block }) => {
  const { state, dispatch } = useBuilder();

  const sortedBlocks = [...state.blocks].sort((a, b) => a.order - b.order);
  const currentIndex = sortedBlocks.findIndex((b) => b.id === block.id);
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === sortedBlocks.length - 1;

  // ── Move helpers ────────────────────────────────────────────────────────

  const canMove = (direction: "up" | "down"): boolean => {
    if (direction === "up" && isFirst) return false;
    if (direction === "down" && isLast) return false;

    // Validate: totals_summary must stay after all line-item blocks
    const newBlocks = [...sortedBlocks];
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    [newBlocks[currentIndex], newBlocks[targetIndex]] = [
      newBlocks[targetIndex],
      newBlocks[currentIndex],
    ];

    const totalsIdx = newBlocks.findIndex((b) => b.blockType === "totals_summary");
    const lastLineIdx = Math.max(
      ...newBlocks.map((b, i) => (LINE_ITEM_TYPES.includes(b.blockType) ? i : -1))
    );

    if (totalsIdx >= 0 && lastLineIdx >= 0 && totalsIdx < lastLineIdx) {
      return false;
    }

    return true;
  };

  const handleMove = (direction: "up" | "down") => {
    if (!canMove(direction)) return;

    const newBlocks = [...sortedBlocks];
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    [newBlocks[currentIndex], newBlocks[targetIndex]] = [
      newBlocks[targetIndex],
      newBlocks[currentIndex],
    ];

    dispatch({ type: "REORDER_BLOCKS", blocks: newBlocks });
  };

  // ── Button styling ──────────────────────────────────────────────────────

  const btnClass =
    "p-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed";

  const activeToggleClass =
    "p-1.5 rounded-lg bg-emerald-50 text-emerald-700 transition-colors";

  const inactiveToggleClass =
    "p-1.5 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors";

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <AnimatePresence>
      <motion.div
        className="absolute -top-12 left-1/2 -translate-x-1/2 z-50 print:hidden"
        initial={{ opacity: 0, y: 8, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 4, scale: 0.98 }}
        transition={{ duration: 0.15 }}
      >
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 px-2 py-1.5 flex items-center gap-1">
          {/* Move up */}
          <button
            type="button"
            className={btnClass}
            disabled={!canMove("up")}
            onClick={() => handleMove("up")}
            title="Move up"
          >
            <ChevronUp className="h-4 w-4" />
          </button>

          {/* Move down */}
          <button
            type="button"
            className={btnClass}
            disabled={!canMove("down")}
            onClick={() => handleMove("down")}
            title="Move down"
          >
            <ChevronDown className="h-4 w-4" />
          </button>

          {/* Divider */}
          <div className="w-px h-5 bg-slate-200" />

          {/* Toggle visibility */}
          <button
            type="button"
            className={block.enabled ? activeToggleClass : btnClass}
            onClick={() => dispatch({ type: "TOGGLE_BLOCK", blockId: block.id })}
            title={block.enabled ? "Hide block" : "Show block"}
          >
            {block.enabled ? (
              <Eye className="h-4 w-4" />
            ) : (
              <EyeOff className="h-4 w-4" />
            )}
          </button>

          {/* Delete */}
          <button
            type="button"
            className={`${btnClass} hover:!text-red-600 hover:!bg-red-50`}
            disabled={block.locked}
            onClick={() => dispatch({ type: "REMOVE_BLOCK", blockId: block.id })}
            title="Delete block"
          >
            <Trash2 className="h-4 w-4" />
          </button>

          {/* ── Image-specific controls ─────────────────────────────────── */}
          {block.blockType === "image" && (
            <>
              {/* Divider */}
              <div className="w-px h-5 bg-slate-200" />

              {/* Width slider */}
              <div className="flex items-center gap-1.5 px-1">
                <span className="text-[10px] font-medium text-slate-400 select-none">W</span>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="5"
                  value={block.settings.imageWidth ?? 100}
                  onChange={(e) =>
                    dispatch({
                      type: "UPDATE_BLOCK_SETTINGS",
                      blockId: block.id,
                      settings: { imageWidth: Number(e.target.value) },
                    })
                  }
                  className="w-16 h-1 accent-emerald-600 cursor-pointer"
                  title={`Width: ${block.settings.imageWidth ?? 100}%`}
                />
                <span className="text-[10px] font-medium text-slate-500 tabular-nums w-7 text-right select-none">
                  {block.settings.imageWidth ?? 100}%
                </span>
              </div>

              {/* Divider */}
              <div className="w-px h-5 bg-slate-200" />

              {/* Alignment buttons */}
              {(["left", "center", "right"] as const).map((align) => {
                const AlignIcon =
                  align === "left"
                    ? AlignLeft
                    : align === "center"
                      ? AlignCenter
                      : AlignRight;
                const isActive = (block.settings.imageAlign ?? "center") === align;

                return (
                  <button
                    key={align}
                    type="button"
                    className={isActive ? activeToggleClass : inactiveToggleClass}
                    onClick={() =>
                      dispatch({
                        type: "UPDATE_BLOCK_SETTINGS",
                        blockId: block.id,
                        settings: { imageAlign: align },
                      })
                    }
                    title={`Align ${align}`}
                  >
                    <AlignIcon className="h-4 w-4" />
                  </button>
                );
              })}
            </>
          )}

          {/* ── Signature-specific controls ─────────────────────────────── */}
          {block.blockType === "signature" && (
            <>
              {/* Divider */}
              <div className="w-px h-5 bg-slate-200" />

              {/* Toggle Name */}
              <button
                type="button"
                className={
                  block.settings.signatureShowName
                    ? activeToggleClass
                    : inactiveToggleClass
                }
                onClick={() =>
                  dispatch({
                    type: "UPDATE_BLOCK_SETTINGS",
                    blockId: block.id,
                    settings: {
                      signatureShowName: !block.settings.signatureShowName,
                    },
                  })
                }
                title="Toggle name"
              >
                <span className="text-xs font-medium px-0.5">Name</span>
              </button>

              {/* Toggle Date */}
              <button
                type="button"
                className={
                  block.settings.signatureShowDate
                    ? activeToggleClass
                    : inactiveToggleClass
                }
                onClick={() =>
                  dispatch({
                    type: "UPDATE_BLOCK_SETTINGS",
                    blockId: block.id,
                    settings: {
                      signatureShowDate: !block.settings.signatureShowDate,
                    },
                  })
                }
                title="Toggle date"
              >
                <span className="text-xs font-medium px-0.5">Date</span>
              </button>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FloatingToolbar;
